import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import axios from "axios";
import { base_url } from "../../config";
import AdminSpinner from "../../components/Spinner/Spinner";
import countryData from './countryData.json';
import { Factory, Presentation } from "lucide-react";

export function getNumericCountryCode(alpha2) {
  const country = countryData.find(
    (entry) => entry['alpha-2'].toUpperCase() === alpha2.toUpperCase()
  );
  return country ? country['country-code'] : null;
}

// Sample data - replace with your actual data
const registrationDataSample = {
  completed: 450,
  students: 180,
  galaDinner: 320,
  studentReception: 150,
  visaLettersSent: 75,
  papersRegistered: 210,
  registrationCodesUsed: 380,


  byType: [
    { name: "Main Conference", value: 300 },
    { name: "Workshops/Tutorials", value: 120 },
    { name: "Industry Day", value: 30 },
  ],

  byCategory: [
    { name: "Regular Delegate", value: 270 },
    { name: "Student with Paper", value: 110 },
    { name: "Student", value: 70 },
  ],

  byPhase: [
    { name: "Early", value: 220 },
    { name: "Late", value: 180 },
    { name: "Onsite", value: 50 },
  ],

  dailyRegistrations: [
    { date: "2023-01-01", count: 12 },
    { date: "2023-01-02", count: 18 },
    { date: "2023-01-03", count: 15 },
    { date: "2023-01-04", count: 25 },
    { date: "2023-01-05", count: 30 },
    { date: "2023-01-06", count: 22 },
    { date: "2023-01-07", count: 18 },
    { date: "2023-01-08", count: 35 },
    { date: "2023-01-09", count: 42 },
    { date: "2023-01-10", count: 28 },
    { date: "2023-01-11", count: 15 },
    { date: "2023-01-12", count: 20 },
    { date: "2023-01-13", count: 25 },
    { date: "2023-01-14", count: 30 },
    { date: "2023-01-15", count: 35 },
  ],

  byCountry: [
    { id: "US", value: 1 },
    { id: "CN", value: 80 },
    { id: "GB", value: 60 },
    { id: "DE", value: 40 },
    { id: "FR", value: 35 },
    { id: "JP", value: 25 },

    // Add more countries as needed
  ],
};

// Color schemes for charts
const COLORS = ["#4f46e5", "#06b6d4", "#f97316", "#8b5cf6", "#ec4899"];

// Custom card component
const Card = ({ title, children, className = "" }) => {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}
    >
      {title && (
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-sm rounded-md">
        <p className="text-sm font-medium">{`${label}`}</p>
        {payload.map((entry, index) => (
          <p
            key={`item-${index}`}
            className="text-sm"
            style={{ color: entry.color }}
          >
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [registrationData, setRegistrationData] = React.useState(
    registrationDataSample
  );
  const [loading, setLoading] = React.useState(true);
  const [countryData, setCountryData] = React.useState({});
  const [tooltipContent, setTooltipContent] = useState("");
  // Stat card component
  const StatCard = ({ title, value, icon, description }) => {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col items-center justify-center">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          {icon && <span className="text-gray-400">{icon}</span>}
        </div>
        {loading ? (
          <>
            <AdminSpinner />
          </>
        ) : (
          <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
        )}
        {description && (
          <p className="mt-1 text-xs text-gray-500 text-center">{description}</p>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${base_url}/api/admin/dashboard`);
        setRegistrationData(response.data);

        const countryDataDict = {};
        response.data.byCountry.forEach((country) => {
          countryDataDict[getNumericCountryCode(country.id)] = country.value;
        });

        setCountryData(countryDataDict);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to format dates for the timeline chart
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Function to get color based on value for choropleth
  const getColorScale = (value) => {
    const maxValue = Math.max(...registrationData.byCountry.map((d) => d.value));
    const minValue = Math.min(...registrationData.byCountry.map((d) => d.value));

    // Normalize between 0 and 1
    const t = (value - minValue) / (maxValue - minValue);

    // Interpolate between lemon yellow and orange
    const r = Math.round(255); // stays constant
    const g = Math.round(244 + t * (140 - 244)); // from 244 ➝ 140
    const b = Math.round(79 + t * (0 - 79));     // from 79 ➝ 0

    return `rgb(${r}, ${g}, ${b})`;
  };




  return (
    <div className="container mx-auto p-4 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col space-y-2">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Completed Registrations"
          value={registrationData.completed}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
          }
          description="Total number of completed registrations"
        />
        <StatCard
          title="Student Registrations"
          value={registrationData.students}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
          }
          description="Total number of student (with discount) registrations"
        />
        <StatCard
          title="Industry Day tickets"
          value={registrationData.industryDay}
          icon={
            <Factory className="h-4" />
          }
          description="Industry Day completed registrations"
        />
        <StatCard
          title="Exhibitors"
          value={registrationData.exhibitors}
          icon={
            <Presentation className="h-4" />
          }
          description="Exhibitors completed registrations"
        />
        {/* <StatCard
          title="Student Reception"
          value={registrationData.studentReception}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          }
          description="Number of seats for student reception"
        /> */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Visa Letters"
          value={registrationData.visaLettersSent}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          }
          description="Visa letters sent"
        />
        <StatCard
          title="Total Papers"
          value={registrationData.totalPapers}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          }
          description="Number of papers needing registrations"
        />
        <StatCard
          title="Registration Codes"
          value={registrationData.registrationCodesUsed}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
          }
          description="Number of registration codes used"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
       
        {/* Registration by Type */}
        <Card title="Location Choices Confirmed">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={registrationData.locationBreakdown}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="value"
                  name="Registrations"
                  fill="#4f46e5"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Registration by Category */}
        <Card title="Paper Location ">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={registrationData.paperLocation}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="value"
                  name="Papers"
                  fill="#06b6d4"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Registration by Phase */}
        <Card title="Social Events Confirmations">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <>
                {registrationData.socialEventConfirmations && (
                  <div className="mb-4">
                    Welcome Reception: {registrationData.socialEventConfirmations['reception-Yes']}<br />
                    Student Reception: {registrationData.socialEventConfirmations['student_reception-Yes']}<br />
                    Banquet: {registrationData.socialEventConfirmations['gala_dinner-Yes']}<br />
                  </div>
                )}
              </>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      {/* Registration Timeline */}
      <Card title="Daily Registrations" className="w-full">
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={registrationData.dailyRegistrations}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12, fill: "#6b7280" }}
              />
              <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                name="Registrations"
                stroke="#4f46e5"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Registration Breakdown Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Registration by Type */}
        <Card title="Registration by Type">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={registrationData.byType}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="value"
                  name="Registrations"
                  fill="#4f46e5"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Registration by Category */}
        <Card title="Registration by Category">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={registrationData.byCategory}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="value"
                  name="Registrations"
                  fill="#06b6d4"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Registration by Phase */}
        <Card title="Registration by Phase">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={registrationData.byPhase}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {registrationData.byPhase.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Choropleth Map */}
      <Card title="Registrations by Country">
        {tooltipContent && (
          <div className="p-4 absolute  text-sm bg-white shadow border rounded">
            {tooltipContent}
          </div>
        )}

        <div className="h-[500px]">
          <ComposableMap projectionConfig={{ scale: 180 }}
            width={800}
            height={500}
            style={{ width: "100%", height: "100%", backgroundColor: "#a0d8f1", borderRadius: "2em" }}>
            <ZoomableGroup>
              <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">

                {({ geographies }) =>
                  geographies.map((geo) => {

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => {
                          const name = geo.properties.name;
                          setTooltipContent(
                            `${name}${countryData[geo.id] !== undefined ? `: ${countryData[geo.id]}` : ": 0"}`
                          );
                        }}
                        onMouseLeave={() => {
                          setTooltipContent("");
                        }}
                        fill={
                          countryData[geo.id]
                            ? getColorScale(countryData[geo.id])
                            : "#ECCB98"
                        }
                        stroke="#777777"

                        style={{

                          default: { outline: "none", opacity: 1 },
                          hover: { outline: "none", opacity: 0.8 },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

        </div>
      </Card>

      {/* Country Breakdown Table */}
      {/* <Card title="Top Countries">
        <div className="space-y-2">
          {registrationData.byCountry
            .sort((a, b) => b.value - a.value)
            .slice(0, 10)
            .map((country, index) => (
              <div
                key={country.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <div className="font-medium text-gray-700">{index + 1}.</div>
                  <div className="text-gray-900">{country.id}</div>
                </div>
                <div className="font-semibold text-gray-900">
                  {country.value}
                </div>
              </div>
            ))}
        </div>
      </Card> */}
    </div>
  );
}
